const normalizeCompanyId = (companyId = 'default') => {
  const value = String(companyId || 'default').trim();
  return value || 'default';
};

const resolveCompanyIdInput = (input = 'default') => {
  if (typeof input === 'object' && input !== null) {
    return normalizeCompanyId(input.companyId);
  }
  return normalizeCompanyId(input);
};

export const COMPANY_COLLECTIONS = Object.freeze({
  CONTACTS: 'contacts',
  ROUTES: 'routes',
  TRANSPORTS: 'transports',
  DRIVERS: 'drivers',
  SETTINGS: 'settings',
  FLEET_EXPENSES: 'fleetExpenses',
  FLEET_EXPENSE_CATEGORIES: 'fleetExpenseCategories',
  SERVICES: 'services',
  SERVICE_TEMPLATES: 'serviceTemplates',
  SERVICE_CONTRACTS: 'serviceContracts',
  SERVICE_CLOSURES: 'serviceClosures',
  SERVICE_LOCATIONS: 'serviceLocations',
  STOCK_PRODUCTS: 'stockProducts',
  STOCK_TASKS: 'stockTasks',
  STOCK_CONTROLS: 'stockControls',
  STOCK_CONTROLLERS: 'stockControllers',
  STOCK_SHEETS: 'stockSheets',
  STOCK_SHEET_LINES: 'stockSheetLines',
  STOCK_PURCHASES: 'stockPurchases',
  STOCK_PURCHASE_LINES: 'stockPurchaseLines',
  STOCK_OUTBOUND_ORDERS: 'stockOutboundOrders',
  STOCK_MOVEMENTS: 'stockMovements',
  SCAN_EVENTS: 'scanEvents',
});

export const COMPANY_SETTINGS_DOCS = Object.freeze({
  MAIN: 'main',
  GENERAL: 'general',
  MODULES: 'modules',
  LOGISTICS_MONITOR: 'logisticsMonitor',
  LIQUIDATION: 'liquidation',
  OFFICE: 'office',
  PLAN: 'plan',
  STOCK: 'stock',
});

export const companiesCollectionPath = () => {
  return 'companies';
};

export const companyRootPath = (companyId = 'default') => {
  return `${companiesCollectionPath()}/${resolveCompanyIdInput(companyId)}`;
};

export const companyCollectionPath = (companyId, collectionName) => {
  if (!collectionName) {
    throw new Error('companyCollectionPath requiere collectionName');
  }

  return `${companyRootPath(companyId)}/${collectionName}`;
};

export const companyDocPath = (companyId, collectionName, docId) => {
  if (!docId) {
    throw new Error('companyDocPath requiere docId');
  }

  return `${companyCollectionPath(companyId, collectionName)}/${docId}`;
};

// =========================
// Empresa
// =========================

export const resolveCompaniesCollectionPath = () => {
  return companiesCollectionPath();
};

export const resolveCompanyDocPath = (companyId) => {
  return companyRootPath(resolveCompanyIdInput(companyId));
};

// =========================
// Settings por empresa
// =========================

export const resolveCompanySettingsCollectionPath = (companyId) => {
  return companyCollectionPath(resolveCompanyIdInput(companyId), COMPANY_COLLECTIONS.SETTINGS);
};

export const resolveCompanySettingsDocPath = (
  companyId,
  settingsDocId = COMPANY_SETTINGS_DOCS.MAIN
) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SETTINGS,
    settingsDocId
  );
};

export const resolveCompanyMainSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.MAIN
  );
};

export const resolveCompanyGeneralSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.GENERAL
  );
};

export const resolveCompanyModulesSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.MODULES
  );
};

export const resolveLogisticsMonitorSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.LOGISTICS_MONITOR
  );
};

export const resolveCompanyLiquidationSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.LIQUIDATION
  );
};

export const resolveCompanyOfficeSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.OFFICE
  );
};

export const resolveCompanyPlanSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.PLAN
  );
};

// =========================
// Colecciones funcionales
// =========================

export const resolveContactsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.CONTACTS
  );
};

export const resolveRoutesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.ROUTES
  );
};

export const resolveRouteDocPath = (companyId, routeId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.ROUTES,
    routeId
  );
};

export const resolveTransportsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.TRANSPORTS
  );
};

export const resolveDriversCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.DRIVERS
  );
};

export const resolveDriverDocPath = (companyId, driverId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.DRIVERS,
    driverId
  );
};

export const resolveFleetExpenseCategoriesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.FLEET_EXPENSE_CATEGORIES
  );
};

export const resolveFleetExpensesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.FLEET_EXPENSES
  );
};

export const resolveServicesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICES
  );
};

export const resolveServiceDocPath = (companyId, serviceId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICES,
    serviceId
  );
};

export const resolveServiceTemplatesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_TEMPLATES
  );
};

export const resolveServiceTemplateDocPath = (companyId, templateId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_TEMPLATES,
    templateId
  );
};

export const resolveServiceContractsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_CONTRACTS
  );
};

export const resolveServiceContractDocPath = (companyId, contractId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_CONTRACTS,
    contractId
  );
};

export const resolveServiceClosuresCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_CLOSURES
  );
};

export const resolveServiceClosureDocPath = (companyId, closureId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_CLOSURES,
    closureId
  );
};

export const resolveServiceLocationsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_LOCATIONS
  );
};

export const resolveServiceLocationDocPath = (companyId, locationId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SERVICE_LOCATIONS,
    locationId
  );
};

export const resolveStockProductsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_PRODUCTS
  );
};

export const resolveStockProductDocPath = (companyId, productId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_PRODUCTS,
    productId
  );
};

export const resolveStockTasksCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_TASKS
  );
};

export const resolveStockTaskDocPath = (companyId, taskId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_TASKS,
    taskId
  );
};

export const resolveStockControlsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_CONTROLS
  );
};

export const resolveStockControlDocPath = (companyId, controlId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_CONTROLS,
    controlId
  );
};


export const resolveStockControllersCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_CONTROLLERS
  );
};

export const resolveStockControllerDocPath = (companyId, controllerId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_CONTROLLERS,
    controllerId
  );
};

export const resolveStockSheetsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_SHEETS
  );
};

export const resolveStockSheetDocPath = (companyId, sheetId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_SHEETS,
    sheetId
  );
};

export const resolveStockSheetLinesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_SHEET_LINES
  );
};

export const resolveStockSheetLineDocPath = (companyId, lineId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_SHEET_LINES,
    lineId
  );
};


export const resolveStockPurchasesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_PURCHASES
  );
};

export const resolveStockPurchaseDocPath = (companyId, purchaseId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_PURCHASES,
    purchaseId
  );
};

export const resolveStockPurchaseLinesCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_PURCHASE_LINES
  );
};

export const resolveStockPurchaseLineDocPath = (companyId, purchaseLineId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_PURCHASE_LINES,
    purchaseLineId
  );
};

export const resolveStockOutboundOrdersCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_OUTBOUND_ORDERS
  );
};

export const resolveStockOutboundOrderDocPath = (companyId, orderId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_OUTBOUND_ORDERS,
    orderId
  );
};

export const resolveStockMovementsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_MOVEMENTS
  );
};

export const resolveStockMovementDocPath = (companyId, movementId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.STOCK_MOVEMENTS,
    movementId
  );
};

export const resolveScanEventsCollectionPath = (companyId) => {
  return companyCollectionPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SCAN_EVENTS
  );
};

export const resolveScanEventDocPath = (companyId, scanEventId) => {
  return companyDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_COLLECTIONS.SCAN_EVENTS,
    scanEventId
  );
};

export const resolveStockSettingsPath = (companyId) => {
  return resolveCompanySettingsDocPath(
    resolveCompanyIdInput(companyId),
    COMPANY_SETTINGS_DOCS.STOCK
  );
};

// =========================
// Compatibilidad legacy mientras migrás el módulo logístico
// =========================

export const legacyContactsCollectionPath = () => 'contacts';

export const legacyRoutesCollectionPath = () => 'routes';

export const legacyRouteDocPath = (routeId) => {
  if (!routeId) {
    throw new Error('legacyRouteDocPath requiere routeId');
  }

  return `routes/${routeId}`;
};

export const legacyTransportsCollectionPath = () => 'transports';

export const legacyLogisticsMonitorSettingsPath = () =>
  `artifacts/default-app/public/data/logisticsSettings/main`;

export const legacyFleetExpenseCategoriesCollectionPath = () =>
  `artifacts/default-app/public/data/fleetExpenseCategories`;

export const legacyFleetExpensesCollectionPath = () =>
  `artifacts/default-app/fleetExpenses`;

export const legacyStockProductsCollectionPath = (appId = 'default-app') =>
  `artifacts/${appId}/products`;

export const legacyStockProductDocPath = (appId = 'default-app', productId) => {
  if (!productId) {
    throw new Error('legacyStockProductDocPath requiere productId');
  }

  return `${legacyStockProductsCollectionPath(appId)}/${productId}`;
};

export const legacyStockTasksCollectionPath = (appId = 'default-app') =>
  `artifacts/${appId}/scheduledTasks`;

export const legacyStockTaskDocPath = (appId = 'default-app', taskId) => {
  if (!taskId) {
    throw new Error('legacyStockTaskDocPath requiere taskId');
  }

  return `${legacyStockTasksCollectionPath(appId)}/${taskId}`;
};

export const legacyStockProductControlsCollectionPath = (appId = 'default-app', productId) => {
  if (!productId) {
    throw new Error('legacyStockProductControlsCollectionPath requiere productId');
  }

  return `${legacyStockProductsCollectionPath(appId)}/${productId}/controls`;
};
